import { getArbitrationMetadata } from 'apis/arbitrationApi'
import { getPurchaseHistory } from 'apis/userApis'
import { PURCHASE_HISTORY_INIT_QUERY } from 'common/constants'
import { createContext, useCallback, useEffect, useMemo, useState } from 'react'
import { IBaseCollectionResponse } from 'types/baseTypes'
import { IArbitrationMetadata } from 'types/arbitrationTypes'
import { IPurchaseHistoryRequest, IPurchaseHistoryResult } from 'types/purchaseHistoryTypes'

export interface IPurchaseHistoryContext {
  searchFilter: IPurchaseHistoryRequest
  updateSearchFilter: (filters: IPurchaseHistoryRequest) => void
  isLoading: boolean
  results: IPurchaseHistoryResult[]
  totalRecords: number
  error: string | null
  hasSearched: boolean
  changePage: (pageNumber: number) => void
  changePageSize: (pageSize: number) => void
  errorMessages: string | null
  isRetailVisible: boolean
  arbitrationMetadata: IArbitrationMetadata | null
  arbitrationMetadataLoading: boolean
}

export const PurchaseHistoryContext = createContext<IPurchaseHistoryContext>(null)

export const PurchaseHistoryProvider = ({ children }) => {
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [response, setResponse] = useState<IBaseCollectionResponse<IPurchaseHistoryResult>>(null)
  const [error, setError] = useState<string | null>(null)
  const [hasSearched, setHasSearched] = useState<boolean>(false)
  const [searchFilter, setSearchFilter] = useState<IPurchaseHistoryRequest>(PURCHASE_HISTORY_INIT_QUERY)
  const [arbitrationMetadata, setArbitrationMetadata] = useState<IArbitrationMetadata | null>(null)
  const [arbitrationMetadataLoading, setArbitrationMetadataLoading] = useState<boolean>(false)

  useEffect(() => {
    fetchArbitrationMetadata()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (searchFilter) {
      fetchPurchaseHistory(searchFilter.Vin != null)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchFilter])

  const fetchArbitrationMetadata = async () => {
    setArbitrationMetadataLoading(true)
    try {
      const metadata = await getArbitrationMetadata()
      setArbitrationMetadata(metadata)
    } catch {
      // silently fail; ArbitrationForm will fall back to its own fetch if metadata is null
    } finally {
      setArbitrationMetadataLoading(false)
    }
  }

  const updateSearchFilter = (filters: IPurchaseHistoryRequest) => {
    setSearchFilter(filters)
  }

  const toApiRequest = (overrides: Partial<IPurchaseHistoryRequest>): IPurchaseHistoryRequest => {
    const sortFieldName = searchFilter.SortBy.Value.split(' ')[0]
    return {
      ...searchFilter,
      ...overrides,
      SortBy: { ...searchFilter.SortBy, Name: sortFieldName, Value: sortFieldName }
    }
  }

  const fetchPurchaseHistory = async (isVinSearch: boolean) => {
    setIsLoading(true)
    setError(null)
    try {
      const apiResult =
        isVinSearch && searchFilter.Vin != null
          ? await getPurchaseHistory(toApiRequest({ FromDate: null, ToDate: null }))
          : await getPurchaseHistory(toApiRequest({ Vin: null }))
      setResponse(apiResult)
      setHasSearched(true)
    } catch (err: any) {
      setError(err?.message ?? 'An error occurred while fetching purchase history.')
    } finally {
      setIsLoading(false)
    }
  }

  const changePage = useCallback((pageNumber: number) => {
    setSearchFilter((prev) => ({ ...prev, PageNumber: pageNumber }))
  }, [])

  const changePageSize = useCallback((pageSize: number) => {
    setSearchFilter((prev) => ({ ...prev, PageSize: pageSize, PageNumber: 0 }))
  }, [])

  const results = useMemo(() => response?.Items ?? [], [response])
  const isRetailVisible = useMemo(() => response?.RetailVisible ?? false, [response])

  const totalRecords = response?.TotalRecords ?? 0
  const errorMessages = response?.ErrorMessages?.join(', ') ?? null
  return (
    <PurchaseHistoryContext.Provider
      value={{
        searchFilter,
        updateSearchFilter,
        isLoading,
        results,
        totalRecords,
        error,
        hasSearched,
        changePage,
        changePageSize,
        errorMessages,
        isRetailVisible,
        arbitrationMetadata,
        arbitrationMetadataLoading
      }}
    >
      {children}
    </PurchaseHistoryContext.Provider>
  )
}
